import type { Ref } from 'vue';
import { computed, ref } from 'vue';

import useAuth from '@/composables/useAuth';
import type { SuggestedFieldType } from '@/constants/data_sources';
import {
  flattenSuggestedFields,
  getRecommendedColumns,
  getRequiredColumns,
  getSuggestedFields,
  isValidSourceField,
  mapSuggestedFieldsToIds,
} from '@/constants/data_sources';
import { TEMP_SMART_FIELD_AND_POP } from '@/constants/feature_flags';
import { captureException } from '@/errors';
import { useFeatureFlagStore } from '@/stores';
import type { Source } from '@/types/sources';
import { isOrgInSmartOnboardingTestGroup } from '@/utils/abTestGate';

type FeedLike = {
  id: number;
  integration: { type: string };
};

export default function useSmartFieldSuggestions(
  feed: Ref<FeedLike | null>,
  sources: Ref<Source[]>,
) {
  const { hasFeatureFlag } = useFeatureFlagStore();
  const { currentOrg } = useAuth();

  const isSmartFieldAndPop = computed(
    () =>
      hasFeatureFlag(TEMP_SMART_FIELD_AND_POP) &&
      isOrgInSmartOnboardingTestGroup(currentOrg.value.id),
  );

  const requiredColumns = ref<Record<string, string[]>>({});
  const fetchedRecommendedColumns = ref<Record<string, string[]>>({});
  const hasFetchedColumns = ref(false);
  const suggestedFieldIds = ref<number[]>([]);
  const hasFetchedSuggestions = ref(false);
  const isFetchingSuggestions = ref(false);
  const lastSuggestedFields = ref<SuggestedFieldType[]>([]);

  async function fetchColumns() {
    if (!feed.value) return;
    const [required, recommended] = await Promise.allSettled([
      getRequiredColumns(
        feed.value as Parameters<typeof getRequiredColumns>[0],
      ),
      getRecommendedColumns(feed.value.integration.type),
    ]);

    if (required.status === 'fulfilled') requiredColumns.value = required.value;
    else captureException(required.reason);

    if (recommended.status === 'fulfilled')
      fetchedRecommendedColumns.value = recommended.value;
    else captureException(recommended.reason);

    hasFetchedColumns.value = true;
  }

  /* If the recommended columns could not be fetched we fall back to the
  required ones, so the preset is never an empty selection. */
  const recommendedColumns = computed(() =>
    Object.keys(fetchedRecommendedColumns.value).length
      ? fetchedRecommendedColumns.value
      : requiredColumns.value,
  );

  async function fetchSuggestions() {
    if (!feed.value || isFetchingSuggestions.value) return;
    isFetchingSuggestions.value = true;
    try {
      const data = await getSuggestedFields(feed.value.id);
      const fields = flattenSuggestedFields(data.populations);
      suggestedFieldIds.value = mapSuggestedFieldsToIds(
        fields,
        sources.value,
        feed.value.integration.type,
      );
      lastSuggestedFields.value = fields;
      hasFetchedSuggestions.value = true;
    } catch (err) {
      captureException(err);
    } finally {
      isFetchingSuggestions.value = false;
    }
  }

  const baseRecommendedFieldIds = computed<number[]>(() => {
    if (!feed.value) return [];
    const columnsByTable = recommendedColumns.value;
    const sourceTables = Object.keys(columnsByTable);
    if (!sourceTables.length) return [];

    const fields = new Set<number>();

    for (const source of sources.value) {
      if (!sourceTables.includes(source.table)) continue;
      for (const field of source.fields) {
        if (!field.id || !isValidSourceField(field)) continue;
        if (columnsByTable[source.table]?.includes(field.column))
          fields.add(field.id);
        if (requiredColumns.value[source.table]?.includes(field.column))
          fields.add(field.id);
      }
    }
    return Array.from(fields);
  });

  /* Required fields are unioned in by baseRecommendedFieldIds, for recommended
  objects only. Adding them for every object on the feed would switch on
  syncing for objects we do not recommend, such as Salesforce Lead. */
  const recommendedFieldIds = computed<number[]>(() => {
    const ids = [...baseRecommendedFieldIds.value];
    if (isSmartFieldAndPop.value && hasFetchedSuggestions.value)
      ids.push(...suggestedFieldIds.value);
    return [...new Set(ids)];
  });

  return {
    isSmartFieldAndPop,
    requiredColumns,
    recommendedColumns,
    hasFetchedColumns,
    suggestedFieldIds,
    hasFetchedSuggestions,
    isFetchingSuggestions,
    lastSuggestedFields,
    baseRecommendedFieldIds,
    recommendedFieldIds,
    fetchColumns,
    fetchSuggestions,
  };
}
